import { Injectable } from '@angular/core';
import { HttpBackend, HttpRequest, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { filter, catchError, switchMap } from 'rxjs/operators';
import { apiResourcesV1 } from './helpers/api-resources';
import { TokenStorageService } from './helpers/token.service';

@Injectable()
export class TokenRefreshService {
  constructor(
    private httpBackend: HttpBackend,
    private tokenStorage: TokenStorageService
  ) {}

  refreshToken(): Observable<any> {
    const refresh = this.tokenStorage.getRefreshToken();
    const token = this.tokenStorage.getToken();

    if (!token) {
      return new Observable((subscriber) => {
        subscriber.error(new Error('No token available'));
      });
    }

    const request = new HttpRequest(
      'POST',
      apiResourcesV1.refreshToken,
      {
        refresh_token: refresh,
        access_token: token,
      },
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      }
    );

    return this.httpBackend.handle(request).pipe(

      filter((event: any): event is HttpResponse<any> => event instanceof HttpResponse),
      switchMap((response: HttpResponse<any>) => {
        const body = response.body;
        
        // Check the response structure
        if (!body?.token) {
          console.error('Invalid token refresh response structure:', body);
          this.tokenStorage.removeToken();
          this.tokenStorage.removeRefreshToken();
          return throwError(() => new Error('Invalid token refresh response: missing token'));
        }

        const tokenData = body.token;

        // Check the presence of access_token (required)
        if (!tokenData.access_token) {
          console.error('Missing access_token in refresh response');
          this.tokenStorage.removeToken();
          this.tokenStorage.removeRefreshToken();
          return throwError(() => new Error('Invalid token refresh response: missing access_token'));
        }

        // Save tokens
        this.tokenStorage.saveToken(tokenData.access_token);

        if (tokenData.refresh_token) {
          this.tokenStorage.saveRefreshToken(tokenData.refresh_token);
        } else {
          console.warn('Missing refresh_token in refresh response');
          this.tokenStorage.removeRefreshToken();
        }

        if (tokenData.refresh_expire) {
          this.tokenStorage.saveExpireRefreshToken(tokenData.refresh_expire);
        }

        if (tokenData.access_expire) {
          this.tokenStorage.saveExpireToken(tokenData.access_expire);
        }

        // Save user data
        this.tokenStorage.saveUser(tokenData);

        return of(body);
      }),
      catchError((error) => {
        console.error('Error in token refresh pipe:', error);
        // If the error is already handled in switchMap, simply pass it through
        if (error instanceof Error && error.message.includes('Invalid token refresh response')) {
          return throwError(() => error);
        }
        // For other errors, also remove tokens
        this.tokenStorage.removeToken();
        this.tokenStorage.removeRefreshToken();
        return throwError(() => error);
      })
    );
  }
}
